import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import Banner from "../components/Header/Banner";
import Foods from "../components/Foods Showcase/Foods";
import Footer from "../components/Footer/Footer";
import StoreClosed from "../components/StoreClosed/StoreClosed";
import { fetchCategories } from "../redux/menu/CategoriesSlice";
import { isStoreOpen } from "../utils/storeTimeUtils";
import { FaBars, FaTimes } from "react-icons/fa";
import useTheme from "../hooks/useTheme";
import {
  clearProducts,
  fetchProducts,
  clearSearchMode,
} from "../redux/menu/ProductSlice";
import { validateCartStore } from "../redux/menu/cartSlice";

export default function HomeScreen() {
  const dispatch = useDispatch();
  const theme = useTheme();
  const { profile } = useSelector((state) => state.profile);
  const {
    products,
    status: productsStatus,
    error: productsError,
    isSearchMode,
    isOffersMode,
  } = useSelector((state) => state.product);
  const { categories } = useSelector((state) => state.category);
  const { banners, status: bannerStatus } = useSelector(
    (state) => state.banner,
  );
  const menuReduxValue = useSelector((state) => state.menu);

  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [menuTab, setMenuTab] = useState(menuReduxValue?.selectedCategory);

  // Check if banner exists
  const bannerList = React.useMemo(
    () => banners?.banner || banners || [],
    [banners],
  );
  const hasBanner =
    (bannerStatus === "succeeded" || bannerStatus === "failed") &&
    bannerList &&
    bannerList.length > 0;

  // Scroll to top when component mounts (when navigating back from details)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Restore category state from sessionStorage on mount
  useEffect(() => {
    const savedCategory = sessionStorage.getItem("savedCategory");
    if (savedCategory && savedCategory !== menuTab) {
      setMenuTab(savedCategory);
    }
  }, []);

  // Fetch profile details when component mounts

  // Fetch categories when component mounts
  useEffect(() => {
    if (profile?.id) {
      dispatch(fetchCategories(profile.id));
    }
  }, [dispatch, profile?.id]);

  // Validate cart store ID when profile loads
  useEffect(() => {
    if (profile?.id) {
      dispatch(validateCartStore(profile.id));
    }
  }, [dispatch, profile?.id]);

  // Handle category selection from modal
  const handleCategorySelect = (categoryName, categoryId) => {
    setMenuTab(categoryName);
    // Clear search mode to reset search state and clear search text in Navbar
    dispatch(clearSearchMode());
    dispatch(clearProducts());
    dispatch(fetchProducts({ categoryID: categoryId, page: 1 }));
    setShowCategoryModal(false);
  };

  // Close modal on Escape key press
  useEffect(() => {
    const handleEscapeKey = (event) => {
      if (event.key === "Escape" && showCategoryModal) {
        setShowCategoryModal(false);
      }
    };

    document.addEventListener("keydown", handleEscapeKey);
    return () => {
      document.removeEventListener("keydown", handleEscapeKey);
    };
  }, [showCategoryModal]);

  // Check if store is currently open
  const storeIsOpen = isStoreOpen(profile?.from_time, profile?.to_time);

  // Check if store status is available (not false)
  const storeStatusAvailable = profile?.status !== false;

  // If store is closed or status is false, show the StoreClosed component
  if (
    (!storeIsOpen && profile?.from_time && profile?.to_time) ||
    !storeStatusAvailable
  ) {
    return <StoreClosed />;
  }

  return (
    <div className="relative min-h-screen bg-white overflow-hidden">
      {/* Watermark background image */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage:
            "url('https://images.unsplash.com/photo-1504674900247-0877df9cc836?auto=format&fit=crop&w=1200&q=80')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          opacity: 0.04, // Extremely subtle opacity for watermark effect
          pointerEvents: "none",
        }}
      />
      {/* Main content */}
      <div className="relative">
        <Banner />

        {/* Search Results - No Products Found */}
        {isSearchMode &&
          productsStatus === "succeeded" &&
          products &&
          products.length === 0 && (
            <div
              className={`container mx-auto px-4 ${!hasBanner ? "pt-20 md:pt-28" : "pt-4"}`}
            >
              <div className="flex items-center justify-center mb-4">
                {/* Category Filter Button */}
                <button
                  onClick={() => setShowCategoryModal(true)}
                  className="cursor-pointer hover:scale-110 transition-transform"
                  title="View All Categories"
                >
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center shadow-md hover:shadow-lg transition-shadow"
                    style={{
                      border: `1px solid ${theme.colors.allCategoriesBorder}`,
                      backgroundColor:
                        theme.colors.allCategoriesBg || "#ffffff",
                    }}
                  >
                    <FaBars
                      className="w-6 h-6"
                      style={{
                        color:
                          theme.colors.menuIconColor ||
                          theme.colors.primaryColor,
                      }}
                    />
                  </div>
                </button>
              </div>
            </div>
          )}

        {/* Search Error */}
        {isSearchMode && productsStatus === "failed" && (
          <div className="text-center py-12">
            <div className="text-red-500 text-lg mb-4">Search failed</div>
            <p className="text-gray-400">Error: {productsError}</p>
          </div>
        )}

        {/* Always use Foods component to display products (regular or search results) */}
        <Foods />

        <Footer />
      </div>

      {/* Category Selection Modal */}
      {showCategoryModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-end justify-center z-50 p-4 pb-32 md:pb-12"
          onClick={() => setShowCategoryModal(false)}
        >
          <div
            className="bg-white rounded-t-xl shadow-2xl w-full max-w-md max-h-[80vh] flex flex-col"
            style={{ backgroundColor: theme.colors.backgroundColor }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div
              className="flex items-center justify-between p-4 border-b"
              style={{ borderColor: theme.colors.borderColor }}
            >
              <button
                onClick={() => setShowCategoryModal(false)}
                className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors"
                style={{ color: theme.colors.textSecondary }}
              >
                <FaTimes className="w-5 h-5" />
              </button>
              <h3
                className="text-lg font-semibold"
                style={{ color: theme.colors.textPrimary }}
              >
                Menu categories
              </h3>
              <div className="w-8"></div> {/* Spacer for centering */}
            </div>

            {/* Modal Content - Vertical List */}
            <div
              className="overflow-y-auto flex-1"
              style={{
                maxHeight: "60vh",
                minHeight: "200px",
                WebkitOverflowScrolling: "touch",
                scrollbarWidth: "thin",
                scrollbarColor: `${theme.colors.borderColor} transparent`,
              }}
            >
              {categories &&
                categories.map((categoryItem, index) => (
                  <button
                    key={categoryItem.id || index}
                    onClick={() => {
                      handleCategorySelect(categoryItem.name, categoryItem.id);
                    }}
                    className="w-full flex items-center justify-between py-4 px-4 hover:bg-gray-50 transition-colors border-l-4 min-h-[60px]"
                    style={{
                      borderLeftColor:
                        menuTab === categoryItem.name
                          ? theme.colors.primaryColor
                          : "transparent",
                      backgroundColor:
                        menuTab === categoryItem.name
                          ? theme.colors.primaryColor + "05"
                          : "transparent",
                    }}
                  >
                    <div className="flex items-center space-x-3">
                      {categoryItem.DocImage?.imageUrl ? (
                        <img
                          src={categoryItem.DocImage.imageUrl}
                          alt={categoryItem.name}
                          className="w-6 h-6 object-contain rounded-full"
                        />
                      ) : (
                        <div className="w-6 h-6 rounded-full flex items-center justify-center">
                          <span className="text-lg">🍽️</span>
                        </div>
                      )}
                      <span
                        className="font-medium"
                        style={{
                          color: theme.colors.textPrimary,
                        }}
                      >
                        {categoryItem.name}
                      </span>
                    </div>
                  </button>
                ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
